(() => {
  'use strict';

  const HEADER_SELECTOR = '[data-tronca-header-v5]';
  const OPEN = 'is-open';
  const OVERLAY_VISIBLE = 'is-visible';
  const BODY_LOCK = 'tronca-v5-panel-open';
  const COMPACT = 'is-compact';
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  const isEl = (value) => value instanceof HTMLElement;

  function getFocusable(container) {
    if (!isEl(container)) return [];
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(container.querySelectorAll(selector)).filter((el) => {
      if (!isEl(el)) return false;
      const style = window.getComputedStyle(el);
      return !el.hidden &&
        el.getAttribute('aria-hidden') !== 'true' &&
        style.display !== 'none' &&
        style.visibility !== 'hidden';
    });
  }

  function shopRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  async function getCart() {
    const response = await fetch(`${shopRoot()}cart.js?t=${Date.now()}`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Impossible de lire le panier.');
    return response.json();
  }

  async function changeCartItem(key, quantity) {
    const response = await fetch(`${shopRoot()}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: key,
        quantity: Math.max(0, Number(quantity) || 0)
      })
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload?.description || payload?.message || 'Mise à jour impossible.');
    return payload;
  }

  function initAll() {
    document.querySelectorAll(HEADER_SELECTOR).forEach((header) => {
      if (isEl(header)) initHeader(header);
    });
  }

  function initHeader(header) {
    if (!isEl(header) || header.dataset.troncaCoreReady === 'true') return;
    header.dataset.troncaCoreReady = 'true';

    const overlay = header.querySelector('[data-tronca-overlay]');
    const mobileMenu = header.querySelector('[data-tronca-mobile-menu]');
    const searchPanel = header.querySelector('[data-tronca-search-panel]');
    const accountPanel = header.querySelector('[data-tronca-account-panel]');
    const cartPanel = header.querySelector('[data-tronca-cart-panel]');
    const mobileTrigger = header.querySelector('[data-tronca-open-mobile-menu]');
    const searchTrigger = header.querySelector('[data-tronca-open-search]');
    const accountTrigger = header.querySelector('[data-tronca-open-account]');
    const cartTrigger = header.querySelector('[data-tronca-open-cart]');
    const searchInput = header.querySelector('[data-tronca-search-input]');
    const standardPanels = [mobileMenu, searchPanel, accountPanel, cartPanel].filter(isEl);
    const compactEnabled = header.dataset.compactScroll === 'true';

    let activePanel = null;
    let activeTrigger = null;
    let previousFocus = null;
    let focusTimer = null;
    let overlayTimer = null;
    let scrollFrame = null;
    let cartBusy = false;

    function setPanel(panel, open) {
      if (!isEl(panel)) return;
      panel.classList.toggle(OPEN, open);
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if ('inert' in panel) panel.inert = !open;
    }

    function setTrigger(trigger, open) {
      if (!isEl(trigger)) return;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function showOverlay() {
      clearTimeout(overlayTimer);
      document.body.classList.add(BODY_LOCK);
      if (!isEl(overlay)) return;
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => overlay.classList.add(OVERLAY_VISIBLE));
    }

    function hideOverlay() {
      document.body.classList.remove(BODY_LOCK);
      if (!isEl(overlay)) return;
      overlay.classList.remove(OVERLAY_VISIBLE);
      overlay.setAttribute('aria-hidden', 'true');
      clearTimeout(overlayTimer);
      overlayTimer = window.setTimeout(() => {
        if (!overlay.classList.contains(OVERLAY_VISIBLE)) overlay.hidden = true;
      }, 280);
    }

    function focusInside(panel, preferred = null) {
      clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        if (isEl(preferred) && !preferred.disabled) {
          preferred.focus({ preventScroll: true });
          return;
        }
        const elements = getFocusable(panel);
        if (elements.length) {
          elements[0].focus({ preventScroll: true });
          return;
        }
        if (isEl(panel)) {
          panel.setAttribute('tabindex', '-1');
          panel.focus({ preventScroll: true });
        }
      }, 90);
    }

    function closePanel({ restore = true } = {}) {
      const trigger = activeTrigger;
      if (isEl(activePanel)) setPanel(activePanel, false);
      setTrigger(activeTrigger, false);
      activePanel = null;
      activeTrigger = null;
      hideOverlay();

      if (restore) {
        const target = isEl(trigger) ? trigger : previousFocus;
        if (isEl(target)) {
          clearTimeout(focusTimer);
          focusTimer = window.setTimeout(() => target.focus({ preventScroll: true }), 70);
        }
      }
    }

    function openPanel(panel, trigger, preferred = null) {
      if (!isEl(panel)) return;

      if (activePanel === panel) {
        closePanel();
        return;
      }

      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (isEl(activePanel)) {
        setPanel(activePanel, false);
        setTrigger(activeTrigger, false);
      }

      activePanel = panel;
      activeTrigger = isEl(trigger) ? trigger : null;
      setTrigger(activeTrigger, true);
      setPanel(panel, true);
      showOverlay();
      focusInside(panel, preferred);
    }

    function trapFocus(event) {
      if (event.key !== 'Tab' || !isEl(activePanel)) return;
      const elements = getFocusable(activePanel);

      if (!elements.length) {
        event.preventDefault();
        activePanel.focus();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function updateCompact() {
      scrollFrame = null;
      header.classList.toggle(COMPACT, compactEnabled && window.scrollY > 36);
    }

    function onScroll() {
      if (scrollFrame !== null) return;
      scrollFrame = requestAnimationFrame(updateCompact);
    }

    function sectionId() {
      return String(header.id || '').replace(/^TroncaHeaderV5-/, '');
    }

    function updateCartCount(cart) {
      const count = Math.max(0, Number.parseInt(String(cart?.item_count ?? 0), 10) || 0);
      header.querySelectorAll('[data-tronca-cart-count]').forEach((counter) => {
        if (!isEl(counter)) return;
        counter.textContent = String(count);
        counter.classList.toggle('is-empty', count === 0);
        counter.setAttribute('aria-label', `${count} produits`);
      });
    }

    async function refreshCartPanel(cart = null) {
      const id = sectionId();
      const drawer = header.querySelector('[data-tronca-cart-panel]');

      if (isEl(drawer) && id) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', id);
          url.searchParams.set('_tronca', String(Date.now()));

          const response = await fetch(url.toString(), {
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            cache: 'no-store'
          });

          if (response.ok) {
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('[data-tronca-cart-panel]');
            if (fresh) drawer.innerHTML = fresh.innerHTML;
          }
        } catch (error) {
          console.warn('[Tronca] cart panel refresh', error);
        }
      }

      const data = cart || await getCart();
      updateCartCount(data);
      document.dispatchEvent(new CustomEvent('tronca:cart-updated', {
        detail: { itemCount: data.item_count, cart: data, source: 'header-core' }
      }));
      return data;
    }

    function openCart() {
      openPanel(cartPanel, cartTrigger);
      refreshCartPanel().catch((error) => console.warn('[Tronca] cart open refresh', error));
    }

    mobileTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      openPanel(mobileMenu, mobileTrigger);
    });

    searchTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      openPanel(searchPanel, searchTrigger, isEl(searchInput) ? searchInput : null);
    });

    accountTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      openPanel(accountPanel, accountTrigger);
    });

    cartTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      openCart();
    });

    header.addEventListener('click', async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const close = target.closest('[data-tronca-close-panel]');
      if (close) {
        event.preventDefault();
        if (isEl(activePanel)) closePanel();
        return;
      }

      const increase = target.closest('[data-tronca-cart-increase]');
      const decrease = target.closest('[data-tronca-cart-decrease]');
      const remove = target.closest('[data-tronca-cart-remove]');

      if (!increase && !decrease && !remove) return;
      event.preventDefault();
      event.stopPropagation();

      if (cartBusy) return;

      const item = target.closest('[data-tronca-cart-item]');
      const input = item?.querySelector('[data-tronca-cart-quantity]');
      const key = item?.dataset.lineKey;

      if (!isEl(item) || !(input instanceof HTMLInputElement) || !key) return;

      const current = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
      const next = increase ? current + 1 : remove ? 0 : Math.max(0, current - 1);

      cartBusy = true;
      item.classList.add('is-updating');

      try {
        const cart = await changeCartItem(key, next);
        await refreshCartPanel(cart);
      } catch (error) {
        console.error('[Tronca] cart mutation', error);
      } finally {
        cartBusy = false;
        item.classList.remove('is-updating');
      }
    }, true);

    if (isEl(overlay)) {
      overlay.addEventListener('click', (event) => {
        event.preventDefault();
        closePanel();
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isEl(activePanel)) {
        event.preventDefault();
        closePanel();
        return;
      }
      trapFocus(event);
    });

    const breakpointChange = (event) => {
      if (event.matches && activePanel === mobileMenu) closePanel({ restore: false });
    };

    if (typeof DESKTOP.addEventListener === 'function') DESKTOP.addEventListener('change', breakpointChange);
    else DESKTOP.addListener(breakpointChange);

    document.addEventListener('tronca:open-cart', openCart);
    document.addEventListener('tronca:open-search', () => openPanel(searchPanel, searchTrigger, isEl(searchInput) ? searchInput : null));
    document.addEventListener('tronca:open-account', () => openPanel(accountPanel, accountTrigger));
    document.addEventListener('tronca:close-panels', () => {
      if (isEl(activePanel)) closePanel({ restore: false });
    });

    document.addEventListener('tronca:refresh-cart-drawer', (event) => {
      const cart = event.detail?.cart || null;
      refreshCartPanel(cart).catch((error) => console.warn('[Tronca] cart event refresh', error));
    });

    document.addEventListener('tronca:cart-updated', (event) => {
      if (event.detail?.source === 'header-core') return;
      const count = Math.max(0, Number.parseInt(String(event.detail?.itemCount), 10) || 0);
      header.querySelectorAll('[data-tronca-cart-count]').forEach((counter) => {
        if (!isEl(counter)) return;
        counter.textContent = String(count);
        counter.classList.toggle('is-empty', count === 0);
      });
    });

    standardPanels.forEach((panel) => setPanel(panel, false));
    [mobileTrigger, searchTrigger, accountTrigger, cartTrigger].forEach((trigger) => setTrigger(trigger, false));

    if (isEl(overlay)) {
      overlay.classList.remove(OVERLAY_VISIBLE);
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
    }

    document.body.classList.remove(BODY_LOCK);

    if (compactEnabled) {
      window.addEventListener('scroll', onScroll, { passive: true });
      updateCompact();
    }

    header.troncaHeaderV5 = {
      openMobileMenu: () => openPanel(mobileMenu, mobileTrigger),
      openSearch: () => openPanel(searchPanel, searchTrigger, isEl(searchInput) ? searchInput : null),
      openAccount: () => openPanel(accountPanel, accountTrigger),
      openCart,
      close: () => {
        if (isEl(activePanel)) closePanel({ restore: false });
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    const header = section.matches(HEADER_SELECTOR) ? section : section.querySelector(HEADER_SELECTOR);
    if (!isEl(header)) return;
    header.dataset.troncaCoreReady = 'false';
    initHeader(header);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    const header = section.matches(HEADER_SELECTOR) ? section : section.querySelector(HEADER_SELECTOR);
    if (isEl(header) && header.troncaHeaderV5) header.troncaHeaderV5.close();
  });
})();
